import { useState, useEffect, useCallback, useRef } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import about1 from '@/assets/about-1.webp';
import about2 from '@/assets/about-2.webp';
import about3 from '@/assets/about-3.webp';
import about4 from '@/assets/about-4.webp';

const images = [about1, about2, about3, about4];

const AboutSection = () => {
  const [current, setCurrent] = useState(0);
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set([0]));
  const [visibleLoaded, setVisibleLoaded] = useState(false);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  // Preload next image
  const preloadImage = useCallback((index: number) => {
    if (loadedImages.has(index)) return;
    const img = new Image();
    img.src = images[index];
    img.onload = () => {
      setLoadedImages(prev => new Set(prev).add(index));
    };
  }, [loadedImages]);

  // Preload next image when current changes
  useEffect(() => {
    const next = (current + 1) % images.length;
    preloadImage(next);
  }, [current, preloadImage]);

  // Auto-advance
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent(prev => {
        const next = (prev + 1) % images.length;
        return next;
      });
    }, 3500);
    return () => clearInterval(timer);
  }, []);

  const handleImageLoad = () => {
    setVisibleLoaded(true);
  };

  const handleDotClick = (i: number) => {
    preloadImage(i);
    setCurrent(i);
    setVisibleLoaded(loadedImages.has(i));
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    touchEndX.current = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      const next = diff > 0
        ? (current + 1) % images.length
        : (current - 1 + images.length) % images.length;
      preloadImage(next);
      setCurrent(next);
      setVisibleLoaded(loadedImages.has(next));
    }
  };

  return (
    <section id="chi-siamo" className="py-20 bg-card">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            Chi Siamo
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">Passione ed esperienza dal 1996.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[2fr_3fr] gap-12 items-center max-w-6xl mx-auto">
          {/* Image Carousel */}
          <div className="rounded-2xl overflow-hidden aspect-[4/3] bg-muted border border-border relative touch-pan-y"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {!visibleLoaded && (
              <Skeleton className="absolute inset-0 w-full h-full rounded-none" />
            )}
            {images.map((src, i) => {
              const isCurrent = i === current;
              const shouldRender = isCurrent || loadedImages.has(i);
              if (!shouldRender) return null;
              return (
                <img
                  key={i}
                  src={src}
                  alt={`Le nostre pizze ${i + 1}`}
                  loading={i === 0 ? 'eager' : 'lazy'}
                  decoding="async"
                  fetchPriority={isCurrent ? 'high' : 'low'}
                  onLoad={isCurrent ? handleImageLoad : undefined}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                    isCurrent ? 'opacity-100' : 'opacity-0'
                  }`}
                />
              );
            })}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
              {images.map((_, i) => (
                <button
                  key={i}
                  onClick={() => handleDotClick(i)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    i === current ? 'bg-primary w-4' : 'bg-foreground/40'
                  }`}
                  aria-label={`Immagine ${i + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Text */}
          <div className="space-y-4 px-0">
            <h3 className="text-2xl font-semibold text-foreground">Oltre 30 anni di passione per la pizza</h3>
            <p className="text-muted-foreground leading-relaxed mx-0 px-0 font-sans text-left">Mi chiamo Alessandro Latorre, faccio il pizzaiolo dal 1996 e da allora la pizza non è mai stata solo un lavoro, ma una vera passione che porto avanti ogni giorno con la stessa cura e lo stesso entusiasmo.</p>
            <p className="text-muted-foreground leading-relaxed">Nel 2009 è nata Pizzeria La Torre, un progetto di famiglia che condivido con mia moglie Manuela e i nostri figli Christian e Nicolò, le altre due torri che danno significato al nostro logo e alla nostra storia.</p>
            <p className="text-muted-foreground leading-relaxed">In questi anni abbiamo creato un luogo semplice e accogliente, dove tradizione, attenzione al cliente e amore per la qualità sono al centro di tutto. Che sia per una cena veloce o per gustare la serata in tranquillità, qui troverete un ambiente semplice, genuino e familiare.</p>
            <p className="text-muted-foreground leading-relaxed font-medium text-foreground">
              Vi aspettiamo in Piazza Maestri Cordai, nella frazione di San Bernardo a Carmagnola.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
